import { Container, IInit } from '../../../common/container/Container'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { FormContainerConfig } from '../container'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { IStatusService } from '../../../common/status/StatusService'
import { STATUS_SERVICE_KEY } from '../../../container/app'

export interface FormStat {
  option: string
  count: number
  percentage: number
}

export interface AlarmStatus {
  medication: boolean
  food: boolean
  sleep: boolean
}

export interface AlarmHistory {
  id: string
  userId: string
  alarmType: string
  triggeredAt: string
  resolvedAt: string | null
  isActive: boolean
}

export interface FormStatByDate {
  date: string
  option: string
  count: number
  percentage: number
}

export interface IAnalyticsApi extends IInit {
  getMedicationStats(userId: string): Observable<FormStat[]>
  getFoodStats(userId: string): Observable<FormStat[]>
  getSleepStats(userId: string): Observable<FormStat[]>
  getDayRatingStats(userId: string): Observable<FormStat[]>
  getAlarmStatus(userId: string): Observable<AlarmStatus>
  getAlarmHistory(userId: string): Observable<AlarmHistory[]>
  getMedicationStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]>
  getFoodStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]>
  getSleepStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]>
  getDayRatingStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]>
}

export class AnalyticsApi implements IAnalyticsApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as FormContainerConfig).moduleFullUrl
  }

  getMedicationStats(userId: string): Observable<FormStat[]> {
    return this._httpClient
      .get<FormStat[]>({
        url: `${this._url}/analytics/medication/${userId}`,
      })
      .pipe(map((response) => response || []))
  }

  getFoodStats(userId: string): Observable<FormStat[]> {
    return this._httpClient
      .get<FormStat[]>({
        url: `${this._url}/analytics/food/${userId}`,
      })
      .pipe(map((response) => response || []))
  }

  getSleepStats(userId: string): Observable<FormStat[]> {
    return this._httpClient
      .get<FormStat[]>({
        url: `${this._url}/analytics/sleep/${userId}`,
      })
      .pipe(map((response) => response || []))
  }

  getDayRatingStats(userId: string): Observable<FormStat[]> {
    return this._httpClient
      .get<FormStat[]>({
        url: `${this._url}/analytics/day-rating/${userId}`,
      })
      .pipe(map((response) => response || []))
  }

  getAlarmStatus(userId: string): Observable<AlarmStatus> {
    return this._httpClient
      .get<AlarmStatus>({
        url: `${this._url}/analytics/alarms/${userId}`,
      })
      .pipe(map((response) => response || { medication: false, food: false, sleep: false }))
  }

  getAlarmHistory(userId: string): Observable<AlarmHistory[]> {
    return this._httpClient
      .get<AlarmHistory[]>({
        url: `${this._url}/analytics/alarm-history/${userId}`,
      })
      .pipe(map((response) => response || []))
  }

  getMedicationStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]> {
    return this._httpClient
      .get<FormStatByDate[]>({
        url: `${this._url}/analytics/medication-range/${userId}?start_date=${startDate}&end_date=${endDate}`,
      })
      .pipe(map((response) => response || []))
  }

  getFoodStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]> {
    return this._httpClient
      .get<FormStatByDate[]>({
        url: `${this._url}/analytics/food-range/${userId}?start_date=${startDate}&end_date=${endDate}`,
      })
      .pipe(map((response) => response || []))
  }

  getSleepStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]> {
    return this._httpClient
      .get<FormStatByDate[]>({
        url: `${this._url}/analytics/sleep-range/${userId}?start_date=${startDate}&end_date=${endDate}`,
      })
      .pipe(map((response) => response || []))
  }

  getDayRatingStatsByRange(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<FormStatByDate[]> {
    return this._httpClient
      .get<FormStatByDate[]>({
        url: `${this._url}/analytics/day-rating-range/${userId}?start_date=${startDate}&end_date=${endDate}`,
      })
      .pipe(map((response) => response || []))
  }
}
